import { useEffect, useState, type FormEvent } from "react";
import type { Item, ItemOption } from "@gift-registry/shared";
import {
  createWishlistItem,
  ApiError,
  deleteWishlistItem,
  importWishlistItemMetadata,
  listWishlistItems,
  updateWishlistItem,
} from "../lib/api.js";
import {
  normalizeItemDraftValues,
  parseItemPrice,
  parseItemQuantity,
  validateItemDraftValues,
  type ItemDraftErrors,
  type ItemDraftValues,
} from "../lib/forms.js";

interface Props {
  wishlistId: string;
  wishlistName: string;
}

const EMPTY_FORM: ItemDraftValues = {
  name: "",
  link: "",
  price: "",
  quantity: "",
  hearts: 0,
  tags: [],
  options: [],
};

export function ItemManager({ wishlistId, wishlistName }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [form, setForm] = useState<ItemDraftValues>(EMPTY_FORM);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ItemDraftErrors>({});
  const [tagInput, setTagInput] = useState("");
  const [optionKey, setOptionKey] = useState("");
  const [optionValue, setOptionValue] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await listWishlistItems(wishlistId);
        if (!mounted) return;
        setItems(response.items);
      } catch (requestError) {
        if (!mounted) return;
        setError(resolveApiMessage(requestError, "Unable to load items"));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void load();

    return () => {
      mounted = false;
    };
  }, [wishlistId]);

  function resetForm(nextItem?: Item) {
    setForm(
      nextItem
        ? {
            name: nextItem.name,
            link: nextItem.link,
            price: nextItem.price === null ? "" : String(nextItem.price / 100),
            quantity: nextItem.quantity === null ? "" : String(nextItem.quantity),
            hearts: nextItem.hearts,
            tags: [...nextItem.tags],
            options: [...nextItem.options],
          }
        : EMPTY_FORM
    );
    setFieldErrors({});
    setTagInput("");
    setOptionKey("");
    setOptionValue("");
    setEditingItemId(nextItem?.id ?? null);
  }

  function appendTag() {
    const nextTag = tagInput.trim();
    if (!nextTag) return;
    setForm((current) => ({
      ...current,
      tags: Array.from(new Set([...current.tags, nextTag])),
    }));
    setTagInput("");
  }

  function appendOption() {
    const key = optionKey.trim();
    const value = optionValue.trim();
    if (!key || !value) return;
    if (key.length > 100 || value.length > 100) {
      setFieldErrors((current) => ({
        ...current,
        options: "Option keys and values must be 100 characters or fewer",
      }));
      return;
    }
    setForm((current) => ({
      ...current,
      options: dedupeOptions([...current.options, { key, value }]),
    }));
    setOptionKey("");
    setOptionValue("");
  }

  async function handleImport() {
    if (!form.link.trim()) {
      setFieldErrors((current) => ({ ...current, link: "Item link is required" }));
      return;
    }

    try {
      const response = await importWishlistItemMetadata(wishlistId, { link: form.link.trim() });
      const metadata = response.result.metadata;
      if (metadata) {
        setForm((current) => ({
          ...current,
          name: metadata.name ?? current.name,
          price: metadata.price === null ? current.price : String(metadata.price / 100),
        }));
        setInfo(response.result.supported ? "Metadata imported." : "Manual editing available.");
      } else {
        setInfo("Metadata import failed. Continue with manual entry.");
      }
      setError("");
    } catch (requestError) {
      setError(resolveApiMessage(requestError, "Unable to import metadata"));
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setInfo("");

    const nextErrors = validateItemDraftValues(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      const normalized = normalizeItemDraftValues(form);
      const payload = {
        name: normalized.name,
        link: normalized.link,
        price: parseItemPrice(normalized.price),
        quantity: parseItemQuantity(normalized.quantity),
        hearts: normalized.hearts,
        tags: normalized.tags,
        options: normalized.options,
      };

      if (editingItemId) {
        const item = items.find((entry) => entry.id === editingItemId);
        if (!item) {
          setError("The selected item is no longer available.");
          return;
        }

        await updateWishlistItem(wishlistId, editingItemId, {
          ...payload,
          version: item.version,
        });
        setInfo("Item updated.");
      } else {
        await createWishlistItem(wishlistId, payload);
        setInfo("Item created.");
      }

      const refreshed = await listWishlistItems(wishlistId);
      setItems(refreshed.items);
      resetForm();
    } catch (requestError) {
      if (isConflict(requestError)) {
        setError("This item changed while you were editing it. Refresh and try again.");
        const refreshed = await listWishlistItems(wishlistId);
        setItems(refreshed.items);
        return;
      }

      setError(resolveApiMessage(requestError, editingItemId ? "Unable to update item" : "Unable to create item"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: Item) {
    if (!window.confirm(`Delete “${item.name}” from ${wishlistName}?`)) {
      return;
    }

    setError("");
    try {
      await deleteWishlistItem(wishlistId, item.id);
      const refreshed = await listWishlistItems(wishlistId);
      setItems(refreshed.items);
      setInfo("Item deleted.");
      if (editingItemId === item.id) {
        resetForm();
      }
    } catch (requestError) {
      setError(resolveApiMessage(requestError, "Unable to delete item"));
    }
  }

  return (
    <section className="card dashboard-manager">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Items</p>
          <h2>{wishlistName}</h2>
        </div>
        <span className="pill">{items.length} items</span>
      </div>

      <div className="manager-grid">
        <form className="manager-panel span-two" onSubmit={handleSubmit}>
          <h3>{editingItemId ? "Edit item" : "Create item"}</h3>
          <div className="manager-item-grid">
            <label className="field compact">
              <span>Name</span>
              <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              {fieldErrors.name ? <strong className="field-error">{fieldErrors.name}</strong> : null}
            </label>
            <label className="field compact">
              <span>Link</span>
              <input value={form.link} onChange={(event) => setForm({ ...form, link: event.target.value })} />
              {fieldErrors.link ? <strong className="field-error">{fieldErrors.link}</strong> : null}
            </label>
            <label className="field compact">
              <span>Price</span>
              <input value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} placeholder="19.99" />
              {fieldErrors.price ? <strong className="field-error">{fieldErrors.price}</strong> : null}
            </label>
            <label className="field compact">
              <span>Quantity</span>
              <input value={form.quantity} onChange={(event) => setForm({ ...form, quantity: event.target.value })} placeholder="1" />
              {fieldErrors.quantity ? <strong className="field-error">{fieldErrors.quantity}</strong> : null}
            </label>
            <label className="field compact">
              <span>Hearts</span>
              <select value={form.hearts} onChange={(event) => setForm({ ...form, hearts: Number(event.target.value) as 0 | 1 | 2 | 3 })}>
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </label>
          </div>

          <div className="manager-item-grid">
            <div className="field compact">
              <span>Tags</span>
              <div className="inline-form">
                <input value={tagInput} onChange={(event) => setTagInput(event.target.value)} placeholder="Add tag" />
                <button type="button" className="secondary-button" onClick={appendTag}>Add</button>
              </div>
              <div className="chip-row">
                {form.tags.map((tag) => (
                  <button key={tag} type="button" className="pill chip" onClick={() => setForm((current) => ({ ...current, tags: current.tags.filter((entry) => entry !== tag) }))}>
                    {tag} ×
                  </button>
                ))}
              </div>
              {fieldErrors.tags ? <strong className="field-error">{fieldErrors.tags}</strong> : null}
            </div>

            <div className="field compact">
              <span>Options</span>
              <div className="inline-form">
                <input value={optionKey} onChange={(event) => setOptionKey(event.target.value)} placeholder="Key" />
                <input value={optionValue} onChange={(event) => setOptionValue(event.target.value)} placeholder="Value" />
                <button type="button" className="secondary-button" onClick={appendOption}>Add</button>
              </div>
              <div className="chip-row">
                {form.options.map((option) => (
                  <button key={`${option.key}:${option.value}`} type="button" className="pill chip" onClick={() => setForm((current) => ({ ...current, options: current.options.filter((entry) => entry.key !== option.key || entry.value !== option.value) }))}>
                    {option.key}: {option.value} ×
                  </button>
                ))}
              </div>
              {fieldErrors.options ? <strong className="field-error">{fieldErrors.options}</strong> : null}
            </div>
          </div>

          <div className="inline-form">
            <button type="button" className="secondary-button" onClick={handleImport}>Import from link</button>
            <button type="submit" className="primary-button" disabled={submitting}>{submitting ? "Saving..." : editingItemId ? "Update item" : "Create item"}</button>
            {editingItemId ? <button type="button" className="ghost-button" onClick={() => resetForm()}>Cancel edit</button> : null}
          </div>
        </form>

        {error ? <div className="card alert-card span-two" role="alert">{error}</div> : null}
        {info ? <div className="card success-card span-two" role="status">{info}</div> : null}

        <div className="manager-panel span-two">
          <h3>Wishlist items</h3>
          {loading ? (
            <p className="muted-copy">Loading items...</p>
          ) : items.length > 0 ? (
            <div className="list-stack">
              {items.map((item) => (
                <article className="list-card" key={item.id}>
                  <strong>{item.name}</strong>
                  <p>{item.link}</p>
                  <p className="muted-copy">Hearts: {item.hearts} · Quantity: {item.quantity ?? 1}</p>
                  {item.tags.length > 0 ? <p className="muted-copy">Tags: {item.tags.join(", ")}</p> : null}
                  {item.options.length > 0 ? <p className="muted-copy">Options: {item.options.map((option) => `${option.key}: ${option.value}`).join(", ")}</p> : null}
                  <div className="list-meta-row">
                    <button type="button" className="secondary-button" onClick={() => resetForm(item)}>Edit</button>
                    <button type="button" className="ghost-button danger-button" onClick={() => void handleDelete(item)}>Delete</button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="muted-copy">No items yet. Create one above.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function dedupeOptions(options: ItemOption[]): ItemOption[] {
  const seen = new Set<string>();
  const deduped: ItemOption[] = [];

  for (const option of options) {
    const key = `${option.key.trim()}\u0000${option.value.trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push({ key: option.key.trim(), value: option.value.trim() });
  }

  return deduped;
}

function resolveApiMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function isConflict(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && (error as ApiError).status === 409;
}